// Notification enums
export enum NotificationType {
  // Task notifications
  TASK_ASSIGNED = 'TASK_ASSIGNED',
  TASK_STATUS_CHANGED = 'TASK_STATUS_CHANGED',
  TASK_DUE_SOON = 'TASK_DUE_SOON',
  TASK_OVERDUE = 'TASK_OVERDUE',
  TASK_SLA_BREACHED = 'TASK_SLA_BREACHED',
  TASK_COMMENT_ADDED = 'TASK_COMMENT_ADDED',
  TASK_PRIORITY_CHANGED = 'TASK_PRIORITY_CHANGED',
  TASK_MENTION = 'TASK_MENTION',

  // Ticket notifications
  TICKET_CREATED = 'TICKET_CREATED',
  TICKET_ASSIGNED = 'TICKET_ASSIGNED',
  TICKET_FOLLOWED = 'TICKET_FOLLOWED',
  TICKET_UPDATED = 'TICKET_UPDATED',
  TICKET_STATUS_CHANGED = 'TICKET_STATUS_CHANGED',
  TICKET_COMMENT_ADDED = 'TICKET_COMMENT_ADDED',
  TICKET_MENTION = 'TICKET_MENTION',

  // Comment/Thread notifications
  COMMENT_MENTION = 'COMMENT_MENTION',
  COMMENT_REPLY = 'COMMENT_REPLY',
  COMMENT_REACTION = 'COMMENT_REACTION',
  THREAD_MESSAGE = 'THREAD_MESSAGE',

  // Project notifications
  PROJECT_MEMBER_ADDED = 'PROJECT_MEMBER_ADDED',
  PROJECT_MEMBER_REMOVED = 'PROJECT_MEMBER_REMOVED',
  PROJECT_STATUS_CHANGED = 'PROJECT_STATUS_CHANGED',
  PROJECT_DUE_SOON = 'PROJECT_DUE_SOON',

  // Sprint notifications
  SPRINT_STARTED = 'SPRINT_STARTED',
  SPRINT_COMPLETED = 'SPRINT_COMPLETED',

  // System notifications
  SUBSCRIPTION_EXPIRING = 'SUBSCRIPTION_EXPIRING',
  SUBSCRIPTION_RENEWED = 'SUBSCRIPTION_RENEWED',
  FEATURE_ANNOUNCEMENT = 'FEATURE_ANNOUNCEMENT',
  SYSTEM_MAINTENANCE = 'SYSTEM_MAINTENANCE',

  // Entity notifications
  ENTITY_MEMBER_ADDED = 'ENTITY_MEMBER_ADDED',
  ENTITY_STATUS_CHANGED = 'ENTITY_STATUS_CHANGED',

  // Deal/Lead notifications
  DEAL_ASSIGNED = 'DEAL_ASSIGNED',
  DEAL_STATUS_CHANGED = 'DEAL_STATUS_CHANGED',
  LEAD_ASSIGNED = 'LEAD_ASSIGNED',
  LEAD_CONVERTED = 'LEAD_CONVERTED',

  // Note notifications
  NOTE_MENTION = 'NOTE_MENTION',
  CLIENT_PORTAL_NOTE_SHARED = 'CLIENT_PORTAL_NOTE_SHARED',

  // Client portal notifications
  CLIENT_PORTAL_TASK_READY = 'CLIENT_PORTAL_TASK_READY',

  // Meeting recording notifications
  MEETING_NOTES_READY = 'MEETING_NOTES_READY',
  MEETING_NOTES_FAILED = 'MEETING_NOTES_FAILED',

  // Import notifications
  IMPORT_COMPLETED = 'IMPORT_COMPLETED',
  IMPORT_FAILED = 'IMPORT_FAILED',

  // Demand notifications
  DEMAND_SUBMITTED = 'DEMAND_SUBMITTED',
  DEMAND_TRIAGED = 'DEMAND_TRIAGED',
  DEMAND_MATCH_AVAILABLE = 'DEMAND_MATCH_AVAILABLE',
  DEMAND_FULFILLED = 'DEMAND_FULFILLED',
  DEMAND_RETURNED_TO_ACTIVE = 'DEMAND_RETURNED_TO_ACTIVE',
  DEMAND_APPROACHING_START = 'DEMAND_APPROACHING_START',
  DEMAND_DEAL_STATUS_CHANGED = 'DEMAND_DEAL_STATUS_CHANGED',

  // Burnout notifications (Plan 9b)
  BURNOUT_RISK_TRANSITION = 'BURNOUT_RISK_TRANSITION',

  // Resource notifications
  RESOURCE_WEEKLY_DIGEST = 'RESOURCE_WEEKLY_DIGEST',

  // Timesheet notifications
  TIMESHEET_ENTRY_SUBMITTED = 'TIMESHEET_ENTRY_SUBMITTED',
  TIMESHEET_ENTRY_APPROVED = 'TIMESHEET_ENTRY_APPROVED',
  TIMESHEET_ENTRY_REJECTED = 'TIMESHEET_ENTRY_REJECTED',
  TIMESHEET_ENTRY_CHANGES_REQUESTED = 'TIMESHEET_ENTRY_CHANGES_REQUESTED',
  TIMESHEET_SUBMITTED = 'TIMESHEET_SUBMITTED',
  TIMESHEET_APPROVED = 'TIMESHEET_APPROVED',
  TIMESHEET_REJECTED = 'TIMESHEET_REJECTED',
  TIMESHEET_CHANGES_REQUESTED = 'TIMESHEET_CHANGES_REQUESTED',
  TIMESHEET_CLOSE_REMINDER = 'TIMESHEET_CLOSE_REMINDER',
  TIMESHEET_MISSING_REMINDER = 'TIMESHEET_MISSING_REMINDER',
  TIMESHEET_APPROVAL_DEADLINE = 'TIMESHEET_APPROVAL_DEADLINE',

  // Client health notifications
  ENTITY_HEALTH_AT_RISK = 'ENTITY_HEALTH_AT_RISK',
  ENTITY_HEALTH_DECLINED = 'ENTITY_HEALTH_DECLINED',
  ENTITY_HEALTH_IMPROVED = 'ENTITY_HEALTH_IMPROVED',
  DEAL_HEALTH_AT_RISK = 'DEAL_HEALTH_AT_RISK',

  // Lifecycle automation notifications
  LIFECYCLE_STAGE_AUTOMATED = 'LIFECYCLE_STAGE_AUTOMATED',

  // Agent review notifications
  AGENT_REVIEW_REQUESTED = 'AGENT_REVIEW_REQUESTED',

  // Personal accounts (family daily-habit loop)
  STREAK_MILESTONE = 'STREAK_MILESTONE',
  LIST_THRESHOLD = 'LIST_THRESHOLD',

  // General
  CUSTOM = 'CUSTOM',
}

export enum NotificationPriority {
  LOW = 'LOW',
  NORMAL = 'NORMAL',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

export enum NotificationStatus {
  UNREAD = 'UNREAD',
  READ = 'READ',
  DISMISSED = 'DISMISSED',
  ARCHIVED = 'ARCHIVED',
}

export enum NotificationChannel {
  IN_APP = 'IN_APP',
  EMAIL = 'EMAIL',
  PUSH = 'PUSH',
  SMS = 'SMS',
  SLACK = 'SLACK',
  WEBHOOK = 'WEBHOOK',
}

// Base notification interface
export interface Notification {
  id: string
  organizationId: string
  recipientId: string
  type: NotificationType
  title: string
  message?: string | null
  data?: any
  entityType?: string | null
  entityId?: string | null
  actorId?: string | null
  priority: NotificationPriority
  status: NotificationStatus
  readAt?: Date | null
  dismissedAt?: Date | null
  createdAt: Date
  updatedAt: Date
  expiresAt?: Date | null

  // Living-notification fields: re-emits coalesce onto one row and bump these
  unreadMessageCount?: number
  lastMessageAt?: Date | null
  lastActorId?: string | null

  // Populated relations
  actor?: {
    id: string
    firstName?: string | null
    lastName?: string | null
    displayName?: string | null
    email: string
    image?: string | null
  }
}

/** Written into `data.resolution` when a notification auto-resolves. */
export interface NotificationResolution {
  outcome: string
  resolvedBy?: string | null
  resolvedAt: string
  auto: boolean
}

// ===== Notification feed (lane-based) =====

/** Types that represent something waiting on the recipient to act. */
export const NOTIFICATION_ACTION_TYPES: NotificationType[] = [
  NotificationType.TIMESHEET_APPROVAL_DEADLINE,
  NotificationType.TIMESHEET_SUBMITTED,
  NotificationType.TIMESHEET_ENTRY_SUBMITTED,
  NotificationType.TIMESHEET_CLOSE_REMINDER,
  NotificationType.TIMESHEET_MISSING_REMINDER,
  NotificationType.TASK_OVERDUE,
  NotificationType.TASK_DUE_SOON,
  NotificationType.TASK_SLA_BREACHED,
  NotificationType.AGENT_REVIEW_REQUESTED,
]

/** Types where someone is speaking directly to the recipient. */
export const NOTIFICATION_MENTION_TYPES: NotificationType[] = [
  NotificationType.TASK_MENTION,
  NotificationType.COMMENT_MENTION,
  NotificationType.NOTE_MENTION,
  NotificationType.TICKET_MENTION,
  NotificationType.COMMENT_REPLY,
]

/**
 * Types a client portal user can receive in-app. Delivery to users without an
 * org membership (portal-only recipients) is limited to this set.
 */
export const CLIENT_PORTAL_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.TASK_MENTION,
  NotificationType.COMMENT_MENTION,
  NotificationType.COMMENT_REPLY,
  NotificationType.NOTE_MENTION,
  NotificationType.TICKET_MENTION,
  NotificationType.THREAD_MESSAGE,
  NotificationType.CLIENT_PORTAL_NOTE_SHARED,
  NotificationType.CLIENT_PORTAL_TASK_READY,
  NotificationType.TASK_ASSIGNED,
  NotificationType.TASK_STATUS_CHANGED,
  NotificationType.TASK_COMMENT_ADDED,
  NotificationType.TICKET_CREATED,
  NotificationType.TICKET_UPDATED,
  NotificationType.TICKET_STATUS_CHANGED,
  NotificationType.TICKET_COMMENT_ADDED,
]

/**
 * Subset of portal types that may go out by email. TICKET_COMMENT_ADDED is
 * deliberately excluded: staff ticket replies already email the requester via
 * the outbound ticket email path, so a notification email would double-send.
 */
export const CLIENT_PORTAL_EMAIL_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.TASK_MENTION,
  NotificationType.COMMENT_MENTION,
  NotificationType.COMMENT_REPLY,
  NotificationType.NOTE_MENTION,
  NotificationType.TICKET_MENTION,
  NotificationType.THREAD_MESSAGE,
  NotificationType.CLIENT_PORTAL_NOTE_SHARED,
  NotificationType.CLIENT_PORTAL_TASK_READY,
]

export interface NotificationFeedCounts {
  /** Unresolved action items (any read state) — drives the red badge */
  actionRequired: number
  /** Unread mentions — drives the blue badge */
  mentions: number
  /** Raw unread, for completeness */
  unread: number
}

export interface NotificationFeedResponse {
  /** Unresolved action items, most urgent first */
  actionRequired: Notification[]
  /** Unread mentions, newest first */
  mentions: Notification[]
  /** Everything else (including recently resolved), newest first */
  activity: Notification[]
  counts: NotificationFeedCounts
  /** Notifications auto-resolved in the last 24h */
  resolvedToday: number
}

// Notification preference interface
export interface NotificationPreference {
  id: string
  userId: string
  organizationId: string
  channel: NotificationChannel
  type: NotificationType
  enabled: boolean
  createdAt: Date
  updatedAt: Date
}

// Notification template interface
export interface NotificationTemplate {
  id: string
  organizationId?: string | null
  type: NotificationType
  channel: NotificationChannel
  name: string
  subject?: string | null
  template: string
  variables?: any
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

export interface PushToken {
  id: string
  userId: string
  token: string
  deviceId?: string | null
  platform?: string | null
  appVersion?: string | null
  lastSeenAt: Date
  createdAt: Date
  updatedAt: Date
}

// Notification creation DTOs
export interface CreateNotificationDto {
  recipientId: string
  type: NotificationType
  title: string
  message?: string
  data?: any
  entityType?: string
  entityId?: string
  actorId?: string
  priority?: NotificationPriority
  expiresAt?: Date
}

export interface BulkCreateNotificationDto {
  recipientIds: string[]
  type: NotificationType
  title: string
  message?: string
  data?: any
  entityType?: string
  entityId?: string
  actorId?: string
  priority?: NotificationPriority
  expiresAt?: Date
}

// Notification update DTOs
export interface UpdateNotificationDto {
  status?: NotificationStatus
  readAt?: Date
  dismissedAt?: Date
}

export interface MarkNotificationsReadDto {
  notificationIds: string[]
}

// Notification preference DTOs
export interface UpdateNotificationPreferenceDto {
  channel: NotificationChannel
  type: NotificationType
  enabled: boolean
}

export interface BulkUpdateNotificationPreferencesDto {
  preferences: UpdateNotificationPreferenceDto[]
}

export interface RegisterPushTokenDto {
  token: string
  deviceId?: string
  platform?: string
  appVersion?: string
}

export interface UnregisterPushTokenDto {
  token: string
}

// Notification query DTOs
export interface NotificationQueryDto {
  status?: NotificationStatus
  type?: NotificationType
  /** Multi-select type filter. Comma-serialized on the wire. */
  types?: NotificationType[]
  priority?: NotificationPriority
  entityType?: string
  entityId?: string
  /** Free-text search across notification title and message. */
  search?: string
  startDate?: Date
  endDate?: Date
  page?: number
  limit?: number
  sortBy?: 'createdAt' | 'priority' | 'status'
  sortOrder?: 'asc' | 'desc'
}

// Notification summary
export interface NotificationSummary {
  total: number
  unread: number
  byType: Record<NotificationType, number>
  byPriority: Record<NotificationPriority, number>
}

// WebSocket event payloads
export interface NotificationCreatedPayload {
  notification: Notification
}

export interface NotificationUpdatedPayload {
  notificationId: string
  updates: Partial<Notification>
}

export interface NotificationDeletedPayload {
  notificationId: string
}

export interface UnreadCountUpdatedPayload {
  count: number
  organizationId: string
  userId: string
}

// Notification context data types
export interface TaskNotificationData {
  taskId: string
  taskName: string
  projectId?: string
  projectName?: string
  previousStatus?: string
  newStatus?: string
  dueDate?: Date
  assigneeId?: string
  assigneeName?: string
}

export interface CommentNotificationData {
  commentId: string
  threadId: string
  threadTitle?: string
  entityType: string
  entityId: string
  mentionedUsers?: string[]
}

export interface ProjectNotificationData {
  projectId: string
  projectName: string
  memberRole?: string
  previousStatus?: string
  newStatus?: string
}

export interface NoteNotificationData {
  noteId: string
  noteTitle: string
  contentSnippet?: string
  mentionedUsers?: string[]
}

export interface SystemNotificationData {
  action: string
  details?: any
  url?: string
}

// Notification action types
export interface NotificationAction {
  label: string
  url?: string
  action?: string
  primary?: boolean
}

// Enhanced notification with actions
export interface NotificationWithActions extends Notification {
  actions?: NotificationAction[]
}

// Plan 9b: Burnout risk transition notification payload
export interface BurnoutRiskTransitionData {
  memberId: string
  memberName: string
  fromLevel: string
  toLevel: string
  streakWeeks: number
  currentUtilizationPct: number
}
